/**
 * Canonical roofing_v1 bot_settings defaults (roofing_bot_settings_defaults.json).
 * id / user_id / email are set by the backend on save.
 * Regenerate ROFING_BOT_SETTINGS_DEFAULTS via: python backend/scripts/export_roofing_defaults_to_js.py
 */

export const CANONICAL_APPOINTMENT_TYPE_KEYS = [
  "free_quote_inspection",
  "phone_video_consult"
];

export const ROOFING_VISIT_APPOINTMENT_TYPE = "free_quote_inspection";
export const ROOFING_CALL_APPOINTMENT_TYPE = "phone_video_consult";

export const LEGACY_APPOINTMENT_TYPE_KEYS = [
  "on_site_inspection",
  "emergency_leak_callout",
  "warranty_callback",
  "annual_maintenance_booking",
  "insurance_assessment",
  "repair_booking",
  "restoration_consult",
  "commercial_site_walk"
];

export const ROOFING_WEEKDAY_ROWS = [
  { key: "monday", label: "Monday" },
  { key: "tuesday", label: "Tuesday" },
  { key: "wednesday", label: "Wednesday" },
  { key: "thursday", label: "Thursday" },
  { key: "friday", label: "Friday" },
  { key: "saturday", label: "Saturday" },
  { key: "sunday", label: "Sunday" },
];

/** @type { bot_type: string, knowledge_base: object, qualification_logic: object, scheduling: object, tone_of_voice: object } */
export const ROOFING_BOT_SETTINGS_DEFAULTS = {
  "bot_type": "roofing_v1",
  "knowledge_base": {
    "company_name": "SkyHigh Roofing Solutions",
    "trading_name": "SkyHigh Roofing Solutions",
    "founded_year": 2010,
    "website_url": "https://skyhigh-roofing.com.au",
    "social_networks": [
      {
        "network": "instagram",
        "url": "https://instagram.com/skyhighroofing",
      },
      {
        "network": "facebook",
        "url": "https://facebook.com/skyhighroofing",
      },
    ],
    "office_location": {
      "address": "120 George Street, Sydney NSW 2000, Australia",
      "is_customer_facing": true,
      "country": "AU",
      "state": "NSW",
    },
    "service_area": {
      "description": "Sydney metropolitan area and surrounding suburbs (NSW)",
      "zip_codes": [
        "2000",
        "2010",
        "2020",
        "2040",
        "2060",
        "2065",
        "2070",
        "2100",
        "2110",
        "2120",
        "2150",
        "2160",
        "2190",
        "2200",
        "2210",
        "2220",
      ],
      "suburbs_or_lgas": "Sydney metro",
      "max_drive_radius_km": 60,
      "crosses_state_borders": false,
      "regional_callouts": {
        "allowed": true,
        "min_job_value": "$2,500",
      },
    },
    "credentials": {
      "registration_number": "ABN 12 345 678 901",
      "license_holder_name": "SkyHigh Roofing Solutions Pty Ltd",
      "license_number": "RBL-NSW-789456",
      "insurance": "Public Liability Insurance – $20M cover",
      "warranty_terms": "10-year workmanship warranty on full roof replacements; 5-year warranty on repairs; manufacturer warranty on all materials.",
    },
    "legal_and_compliance": {
      "privacy_policy_url": "https://skyhigh-roofing.com.au/privacy",
      "state_regulations": "By NSW law, jobs over $5,000 include a 5-day cooling-off period.",
    },
    "operations": {
      "quoting_workflow": {
        "on_site_quoter_role": "Dedicated Estimator",
        "follow_up_booking_flow": "office_routing",
      },
    },
    "handoff_settings": {
      "roles": [
        {
          "type": "general",
          "role": "Office Manager",
          "name": "Sarah",
          "phone": "+61 2 9999 0001",
          "email": "sarah@skyhigh-roofing.com.au",
        },
        {
          "type": "emergency",
          "role": "On-call Technician",
          "name": "Mike",
          "phone": "+61 400 000 999",
          "email": "",
        },
      ],
      "handoff_triggers": [
        "customer_requests_human",
        "aggressive_behavior",
        "complex_insurance_claim",
      ],
      "handoff_messages": {
        "general_escalation": "I'll have Sarah, our office manager, take over from here.",
        "emergency_escalation": "I'm routing you directly to our on-call technician Mike right now.",
      },
    },
    "pricing_policy": {
      "quote_rules": {
        "free_quotes_standard": true,
      },
      "job_requirements": {
        "minimum_job_value": "$500",
        "deposit_required": "10% upon acceptance",
      },
      "payment_and_finance": {
        "tax_status": "GST inclusive",
        "payment_methods": [
          "Bank Transfer",
          "Credit Card (Mastercard/Visa)",
          "Cash",
        ],
      },
      "ballpark_pricing": {
        "products": [
          {
            "product_name": "Full Colorbond Roof Replacement",
            "pricing": "$150 – $220 per sqm (supply & install)",
          },
          {
            "product_name": "Tile Roof Repair",
            "pricing": "$300 – $800 per job depending on scope",
          },
          {
            "product_name": "Roof Inspection & Quote",
            "pricing": "FREE – no obligation",
          },
          {
            "product_name": "Emergency Leak Repair",
            "pricing": "$350 – $600 call-out fee (same-day)",
          },
        ],
      },
    },
    "services_offered": {
      "metal_roof_replacement": "true",
      "tile_roof_replacement": "true",
      "slate_roof_work": "false",
      "roof_restoration_repaint_rebed": "sometimes",
      "roof_repairs_leak_detection": "true",
      "storm_hail_damage_repair": "true",
      "insurance_claim_work": "sometimes",
      "reroofing_over_existing": "false",
      "new_build_roofing": "true",
      "strata_multi_dwelling_roofing": "false",
      "gutters_fascia_downpipes": "true",
      "gutter_guard": "sometimes",
      "skylights": "true",
      "whirlybirds_ventilation": "true",
      "roof_painting": "false",
      "solar_panel_removal_refit": "false",
      "insulation": "false",
      "asbestos_roof_removal": "false",
      "heritage_specialist_work": "sometimes",
      "commercial_industrial_roofing": "false",
      "annual_maintenance": "true",
      "other_services": {
        "status": "true",
        "description": "Chimney flashing repairs, custom metal capping, box-gutter replacements",
      },
    },
    "objection_handling": [
      {
        "objection": "Price is too high / I found it cheaper",
        "response": "We use premium Colorbond and tile systems with proper waterproofing — cutting corners on flashing is where cheap quotes fail within a few years.",
      },
    ],
    "competitive_strategy": {
      "competitors_often_mentioned": [
        "Local Roofers Co",
        "Cheap Roofs AU",
      ],
      "core_differentiator": "10-year workmanship warranty on full replacements and same-day emergency leak call-outs across Sydney metro.",
    },
    "last_rag_index_update": "2026-04-25T10:00:00Z",
  },
  "qualification_logic": {
    "questions": [
      {
        "question": "What's the full address of the property?",
        "key": "property_address",
        "internal_goal": "Service area check and logistics planning",
        "turn_on": true,
        "optional": false,
      },
      {
        "question": "Are you the owner, tenant, or an agent for this property?",
        "key": "ownership_status",
        "internal_goal": "Identify decision-maker authority",
        "turn_on": true,
        "optional": false,
      },
      {
        "question": "Is this a house, townhouse, unit, commercial building, or strata property?",
        "key": "property_type",
        "internal_goal": "Determine equipment needs and scale",
        "turn_on": true,
        "optional": false,
      },
      {
        "question": "Is the property single, double, or triple storey?",
        "key": "storey_count",
        "internal_goal": "Determine ladder height and safety requirements",
        "turn_on": true,
        "optional": false,
      },
      {
        "question": "Do you know what material the roof is currently made of (e.g., tile, metal)?",
        "key": "roof_type",
        "internal_goal": "Identify roof material for scope assessment",
        "turn_on": true,
        "optional": true,
      },
      {
        "question": "Roughly how old is the roof?",
        "key": "roof_age",
        "internal_goal": "Identify potential for full replacement vs repair",
        "turn_on": true,
        "optional": true,
      },
      {
        "question": "What specifically is happening with the roof? What do you need help with?",
        "key": "issue_details",
        "internal_goal": "Categorize the primary problem (leak, damage, aesthetic)",
        "turn_on": true,
        "optional": false,
      },
      {
        "question": "How urgent is this? Do you have an active leak now, or are you planning ahead?",
        "key": "timeline_urgency",
        "internal_goal": "Prioritize emergency callouts vs standard quotes",
        "turn_on": true,
        "optional": false,
      },
      {
        "question": "Has this been quoted before? If so, do you mind sharing by whom?",
        "key": "previous_quotes",
        "internal_goal": "Assess price sensitivity and competition",
        "turn_on": false,
        "optional": true,
      },
      {
        "question": "Is there an insurance claim involved? If yes, what is the insurer's name and claim number?",
        "key": "insurance_status",
        "internal_goal": "Identify workflow (Assessor report vs private quote)",
        "turn_on": true,
        "optional": true,
      },
      {
        "question": "Do you have any photos of the roof or the damage that you could upload?",
        "key": "has_photos",
        "internal_goal": "Gather visual evidence for remote estimation",
        "turn_on": true,
        "optional": true,
      },
      {
        "question": "What is the best way to contact you (phone, SMS, or email)?",
        "key": "preferred_contact_method",
        "internal_goal": "Set communication channel for follow-up",
        "turn_on": true,
        "optional": false,
      },
      {
        "question": "What is the best time of day to reach you?",
        "key": "preferred_callback_time",
        "internal_goal": "Coordinate callback scheduling",
        "turn_on": true,
        "optional": false,
      },
      {
        "question": "Just out of curiosity, how did you hear about us?",
        "key": "lead_source",
        "internal_goal": "Marketing source attribution",
        "turn_on": false,
        "optional": true,
      },
      {
        "question": "Do you have a specific budget range in mind for this project?",
        "key": "budget_range",
        "internal_goal": "Qualify financial expectations",
        "turn_on": false,
        "optional": true,
      },
    ],
    interviewer_policy: {
      repetition_soft_ask_threshold: 3,
      refusal_escalation_threshold: 2,
    },
    "unqualified_action": "manager",
    "strict_qualification_gate": true,
    "disqualification_rules": {
      "blocked_property_types": [
        "commercial",
        "strata",
      ],
      "max_storeys": 2,
      "tenant_policy": "require_owner_consent",
      "asbestos_policy": "refer_to_licensed_asbestos_specialist",
      "custom_auto_declines": [
        "Heritage listed buildings",
        "Thatch roofs",
      ],
      "out_of_scope_services": [
        "asbestos_removal",
        "large_commercial_flat_roofs_over_2000sqm",
        "solar_install",
        "roof_painting",
        "Solar panel installation and cleaning",
        "Internal plumbing and pipe repairs",
      ],
      "rejection_messages": {
        "out_of_area": "We currently service the Sydney metro area only. We'd recommend contacting a local licensed roofer in your area.",
        "service_not_offered": "While we'd love to help, our crews don't currently handle that specific type of work.",
        "property_type": "Our team currently specializes in residential properties up to 2 storeys.",
        "tenant_no_authority": "Since you are renting the property, we require direct authorization from the homeowner or property manager before we can provide an estimate.",
        "asbestos_referral": "Based on the details, there might be asbestos present. We recommend contacting a licensed asbestos removal specialist.",
        "height_limit": "For safety reasons, our crews can only service buildings up to 2 storeys high.",
        "budget_too_low": "Thanks for sharing your budget — our projects typically start at a higher minimum. We may not be the right fit for this scope, but we appreciate you reaching out.",
        "default_polite_no": "Thank you for considering us, but it doesn't look like we are the right fit for your project at this time.",
      },
    },
  },
  "scheduling": {
    "operating_hours": {
      "custom_schedule_rules": [
        "No bookings on Fridays",
      ],
    },
    "channel_schedules": {
      "site_visit": {
        "monday": {
          "is_open": true,
          "start": "07:00",
          "end": "16:00",
        },
        "tuesday": {
          "is_open": true,
          "start": "07:00",
          "end": "16:00",
        },
        "wednesday": {
          "is_open": true,
          "start": "07:00",
          "end": "16:00",
        },
        "thursday": {
          "is_open": true,
          "start": "07:00",
          "end": "16:00",
        },
        "friday": {
          "is_open": false,
          "start": null,
          "end": null,
        },
        "saturday": {
          "is_open": false,
          "start": null,
          "end": null,
        },
        "sunday": {
          "is_open": false,
          "start": null,
          "end": null,
        },
      },
      "phone_consult": {
        "monday": {
          "is_open": true,
          "start": "08:30",
          "end": "17:00",
        },
        "tuesday": {
          "is_open": true,
          "start": "08:30",
          "end": "17:00",
        },
        "wednesday": {
          "is_open": true,
          "start": "08:30",
          "end": "17:00",
        },
        "thursday": {
          "is_open": true,
          "start": "08:30",
          "end": "17:00",
        },
        "friday": {
          "is_open": true,
          "start": "08:30",
          "end": "17:00",
        },
        "saturday": {
          "is_open": true,
          "start": "09:00",
          "end": "13:00",
        },
        "sunday": {
          "is_open": false,
          "start": null,
          "end": null,
        },
      },
    },
    "logistics": {
      "travel_buffer_minutes": 30,
      "appointment_time_format": "arrival_window",
      "window_size_hours": 2,
    },
    "team_calendars": [
      {
        "staff_name": "Main GHL Calendar",
        "calendar_id": "",
        "handles_appointment_types": [
          "free_quote_inspection",
          "phone_video_consult",
        ],
      },
    ],
    "booking_rules": {
      "visit_min_lead_time_hours": 24,
      "call_min_lead_time_hours": 4,
      "max_lead_time_days": 30,
      "emergency_dispatch_enabled": false,
      "emergency_min_lead_time_hours": 1,
    },
    "booking_channel": {
      "visit_type": "free_quote_inspection",
      "call_type": "phone_video_consult",
    },
    "appointment_types": {
      "free_quote_inspection": {
        "is_offered": true,
        "label": "Free Inspection",
        "duration_minutes": 60,
        "is_free": true,
        "price": null,
        "attendee": "field_technician",
        "prerequisites": [
          "address",
          "problem description",
        ],
      },
      "phone_video_consult": {
        "is_offered": true,
        "label": "Phone / Video Consult",
        "duration_minutes": 30,
        "is_free": true,
        "price": null,
        "attendee": "office_staff",
        "prerequisites": [
          "phone number",
        ],
      },
    },
  },
  "tone_of_voice": {
    "bot_name": "Max",
    "disclose_ai_status": true,
    "style": "blue_collar_professional",
    "use_emojis": false,
    "use_aussie_slang": false,
    "forbidden_phrases": [
      "guarantee",
      "100% covered by insurance",
      "we promise",
      "insurance will definitely pay",
    ],
    "preferred_phrases": [
      "We've got your roof covered",
      "Built to withstand Aussie weather",
    ],
  },
};

export const ROOFING_DEFAULT_CHANNEL_SCHEDULES = JSON.parse(
  JSON.stringify(ROOFING_BOT_SETTINGS_DEFAULTS.scheduling.channel_schedules)
);

export const ROOFING_SCHEDULING_DEFAULTS = JSON.parse(
  JSON.stringify(ROOFING_BOT_SETTINGS_DEFAULTS.scheduling)
);

export function cloneRoofingDefaults() {
  return JSON.parse(JSON.stringify(ROOFING_BOT_SETTINGS_DEFAULTS));
}
